import React from 'react'
import { Button, Card, Form, Image, Input, Modal, Popconfirm, Select, Space, Switch, Table, Tag, Typography, message } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { listShopTags, createShopTag, updateShopTag, deleteShopTag } from '../../api/shopTags'

const DISPLAY_MODE_OPTIONS = [
  { value: 'SHOP_LIST', label: 'قائمة متاجر (SHOP_LIST)' },
  { value: 'UNIFIED_CATALOG', label: 'كتالوج موحد (UNIFIED_CATALOG)' },
]

export default function ShopsTagsAdmin() {
  const [data, setData] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [creating, setCreating] = React.useState(false)
  const [createOpen, setCreateOpen] = React.useState(false)
  const [editOpen, setEditOpen] = React.useState(false)
  const [editing, setEditing] = React.useState(false)
  const [selected, setSelected] = React.useState(null)

  const [createForm] = Form.useForm()
  const [editForm] = Form.useForm()

  const fetchData = React.useCallback(async () => {
    setLoading(true)
    try {
      const items = await listShopTags()
      setData(items || [])
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => { fetchData() }, [fetchData])

  function parseError(e) {
    const t = (e && e.message) ? e.message : 'Request failed'
    return t.replace(/^\s*"?|"?\s*$/g, '')
  }

  async function onCreate(values) {
    setCreating(true)
    try {
      const payload = {
        tagAr: values.tagAr?.trim() || undefined,
        imageUrl: values.imageUrl?.trim() || undefined,
        hidden: values.hidden ?? false,
        displayMode: values.displayMode || 'SHOP_LIST',
      }
      await createShopTag(payload)
      message.success('تم الإنشاء')
      setCreateOpen(false)
      createForm.resetFields()
      await fetchData()
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    } finally {
      setCreating(false)
    }
  }

  function openEdit(record) {
    setSelected(record)
    editForm.setFieldsValue({
      tagAr: record.tagAr,
      imageUrl: record.imageUrl || '',
      hidden: !!record.hidden,
      displayMode: record.displayMode || 'SHOP_LIST',
    })
    setEditOpen(true)
  }

  async function onEdit(values) {
    if (!selected) return
    setEditing(true)
    try {
      const payload = {}
      if (values.tagAr !== selected.tagAr) payload.tagAr = values.tagAr?.trim()
      if (values.imageUrl !== (selected.imageUrl || '')) payload.imageUrl = values.imageUrl?.trim()
      if (!!values.hidden !== !!selected.hidden) payload.hidden = !!values.hidden
      if (values.displayMode !== (selected.displayMode || 'SHOP_LIST')) payload.displayMode = values.displayMode
      if (Object.keys(payload).length === 0) {
        message.info('لا تغييرات للحفظ')
      } else {
        await updateShopTag(selected.id, payload)
        message.success('تم التحديث')
      }
      setEditOpen(false)
      setSelected(null)
      await fetchData()
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    } finally {
      setEditing(false)
    }
  }

  async function onDelete(id) {
    try {
      await deleteShopTag(id)
      message.success('تم الحذف')
      await fetchData()
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    }
  }

  const columns = [
    { title: 'رقم', dataIndex: 'id', key: 'id', width: 80 },
    { title: 'الاسم', dataIndex: 'tagAr', key: 'tagAr' },
    {
      title: 'الصورة', dataIndex: 'imageUrl', key: 'imageUrl', width: 100,
      render: (v) => v ? (
        <Image alt="" src={`https://images.leen-go.com/${v}`} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4 }} fallback="https://placehold.co/48x48?text=Error" />
      ) : '-'
    },
    {
      title: 'نوع العرض', dataIndex: 'displayMode', key: 'displayMode', width: 160,
      render: (v) => v === 'UNIFIED_CATALOG'
        ? <Tag color="purple">كتالوج موحد</Tag>
        : <Tag color="blue">قائمة متاجر</Tag>
    },
    {
      title: 'مفتوح', dataIndex: 'isOpened', key: 'isOpened', width: 100,
      render: (v) => v ? <Tag color="green">مفتوح</Tag> : <Tag>مغلق</Tag>
    },
    {
      title: 'مخفي', dataIndex: 'hidden', key: 'hidden', width: 100,
      render: (v) => v ? <Tag color="red">مخفي</Tag> : <Tag color="blue">ظاهر</Tag>
    },
    {
      title: '', key: 'actions', width: 200,
      render: (_, record) => (
        <Space>
          <Button onClick={() => openEdit(record)}>تعديل</Button>
          <Popconfirm title="حذف هذا التصنيف؟" onConfirm={() => onDelete(record.id)}>
            <Button danger>حذف</Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div style={{ maxWidth: 1000, margin: '40px auto', padding: '0 16px' }}>
      <Space style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>التصنيفات الرئيسية</Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>تصنيف جديد</Button>
      </Space>
      <Card>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={{ pageSize: 20 }}
        />
      </Card>

      <Modal
        title="إنشاء تصنيف"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        okText="إنشاء"
        confirmLoading={creating}
        onOk={() => createForm.submit()}
      >
        <Form form={createForm} layout="vertical" onFinish={onCreate} initialValues={{ displayMode: 'SHOP_LIST', hidden: false }}>
          <Form.Item label="الاسم" name="tagAr" rules={[{ required: true, message: 'يرجى إدخال الاسم' }]}>
            <Input placeholder="مثال: سوبرماركت" />
          </Form.Item>
          <Form.Item label="رابط الصورة" name="imageUrl">
            <Input placeholder="categories/supermarket.jpg" />
          </Form.Item>
          <Form.Item label="نوع العرض" name="displayMode" rules={[{ required: true }]}>
            <Select options={DISPLAY_MODE_OPTIONS} />
          </Form.Item>
          <Form.Item label="مخفي" name="hidden" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="تعديل التصنيف"
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        okText="حفظ"
        confirmLoading={editing}
        onOk={() => editForm.submit()}
      >
        <Form form={editForm} layout="vertical" onFinish={onEdit}>
          <Form.Item label="الاسم" name="tagAr">
            <Input />
          </Form.Item>
          <Form.Item label="رابط الصورة" name="imageUrl">
            <Input placeholder="categories/supermarket.jpg" />
          </Form.Item>
          <Form.Item label="نوع العرض" name="displayMode">
            <Select options={DISPLAY_MODE_OPTIONS} />
          </Form.Item>
          <Form.Item label="مخفي" name="hidden" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
